/**
 * What the Overview map's heading says about the feed (twin slice 1, plan
 * W3 step 11).
 *
 * The heading's dot used to pulse whatever was happening: a dead stream,
 * a woken tab and a healthy feed all read "live". It now follows
 * FeedHealth, the same answer that desaturates the canvas and dims every
 * robot (`feed-health.ts`), so the heading can never claim more than the
 * map under it.
 *
 * WORDS: "Reconnecting · last update HH:MM" for every reason the feed is
 * clamped, and "Reconnecting" alone when there has never been an update
 * to name. These are the plan's words (W3 acceptance) and the verdict
 * bar's (`attention/select.ts`, `Reconnecting · last update ...`): the
 * heading and the verdict sit on the same page and read the same
 * FeedHealth, so they say the same thing. A `silent` feed (heartbeat up,
 * no data for 15 s) is not strictly reconnecting, and a first load has
 * nothing to RE-connect; telling those apart is one decision for both
 * places, not one this heading takes alone.
 *
 * HH:MM is on the SITE's clock, with the day named when it is not today
 * ("yesterday 23:58"), and marked "(device time)" when the site has no
 * declared zone (`formatClock`). Null when the feed is not clamped: the
 * pulsing dot is the whole message then.
 */
import type { FeedHealth } from '../../api/feed-health';
import { formatClock } from '../Devices/lift-reading';

export function feedHeadingWords(
  feed: FeedHealth,
  /** The site's declared zone; null when it has none (device time). */
  timeZone: string | null,
  /** `Date.now()`, read only to name the day (`formatClock`). */
  wallNowMs: number,
): string | null {
  if (!feed.clamped) return null;
  if (feed.lastUpdateAtMs === null) return 'Reconnecting';
  return `Reconnecting · last update ${formatClock(feed.lastUpdateAtMs, timeZone, wallNowMs)}`;
}
